// import './wdyr'; // <--- needs to be first import

import React from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import './App.css'
import '@fontsource-variable/plus-jakarta-sans'
import '@fontsource/jetbrains-mono/400.css'
import '@fontsource/jetbrains-mono/500.css'
import '@fontsource/jetbrains-mono/600.css'
import '@fontsource/jetbrains-mono/700.css'
import Box from '@mui/material/Box'
import { createTheme, ThemeProvider } from '@mui/material'
import { ApplicationSuiteProvider } from './contexts/ApplicationSuiteProvider'
import MainLayout from './layout/MainLayout'
import { SnackbarHelper } from './contexts/SnackbarHelperContext'
import GreenChartSubscriptionRouteGuard from './guards/GreenChartSubscriptionRouteGuard'
import { TutorialProvider } from './contexts/TutorialContext'
import AutoTradeLeaderboard from './app/leaderboard/AutoTradeLeaderboard'

declare module '@mui/material/styles' {
  interface TypeBackground {
    custom1?: string
    custom2?: string
    custom3?: string
    custom4?: string
  }

  interface TypeAction {
    main?: string
    background?: string
    idleBorder?: string
    idle?: string
  }

  interface PaletteColor {
    main: string
    background?: string
    disabled?: string
    idleBorder?: string
    idle?: string
  }

  interface SimplePaletteColorOptions {
    main: string
    background?: string
    disabled?: string
    idleBorder?: string
    idle?: string
  }

  interface PaletteColorOptions {
    main?: string
    background?: string
    disabled?: string
    idleBorder?: string
    idle?: string
  }

  interface Palette {
    danger: PaletteColor
    cyan: PaletteColor
    green: PaletteColor
    idleBorder?: string
    idle?: string
  }

  interface PaletteOptions {
    danger?: PaletteColorOptions
    cyan?: PaletteColorOptions
    green?: PaletteColorOptions
    idleBorder?: string
    idle?: string
  }
}

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#1A1B1E',
      paper: '#232529',
      custom1: '#1A1B1E',
      custom2: '#232529',
      custom3: '#2C2E33',
      custom4: '#383B42',
    },
    divider: 'rgba(255, 255, 255, 0.06)',
    primary: {
      main: '#1DC30E',
    },
    secondary: {
      main: '#1DC30E',
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.92)',
      secondary: 'rgba(255, 255, 255, 0.55)',
    },
    action: {
      main: '#00a1ff',
      idle: 'rgba(0, 161, 255, .6)',
      background: 'rgba(0, 161, 255, .08)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      idleBorder: 'rgba(0, 161, 255, .3)',
      hover: 'rgba(255, 255, 255, 0.04)',
    },
    success: {
      main: '#00ff5c',
      idle: 'rgba(0, 255, 141, .6)',
      background: 'rgba(0, 255, 141, .07)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      idleBorder: 'rgba(0, 255, 141, .3)',
    },
    danger: {
      main: '#f44336',
      idle: 'rgba(244, 67, 54, .6)',
      background: 'rgba(244, 67, 54, .07)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      idleBorder: 'rgba(244, 67, 54, .3)',
    },
    cyan: {
      main: '#00bcd4',
      idle: 'rgba(0, 188, 212, .6)',
      background: 'rgba(0, 188, 212, .07)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      idleBorder: 'rgba(0, 188, 212, .3)',
    },
    green: {
      main: '#1DC30E',
      idle: 'rgba(29, 195, 14, .6)',
      background: 'rgba(29, 195, 14, .07)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      idleBorder: 'rgba(29, 195, 14, .3)',
    },
  },
  typography: {
    fontFamily: "'Plus Jakarta Sans Variable', 'Plus Jakarta Sans', sans-serif",
    h6: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    subtitle1: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.01em',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 0 20px rgba(29, 195, 14, 0.15)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          letterSpacing: '0.02em',
          fontWeight: 600,
          textTransform: 'none',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.16)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.06)',
        },
      },
    },
  },
})

function App() {
  return (
    <Router>
      <Routes>
        {/* Public leaderboard — no auth guard, safe to iframe in 3rd-party apps */}
        <Route
          path="/auto-trade-leaderboard"
          element={
            <ThemeProvider theme={theme}>
              <AutoTradeLeaderboard />
            </ThemeProvider>
          }
        />
        <Route
          path="/*"
          element={
            <ThemeProvider theme={theme}>
              <GreenChartSubscriptionRouteGuard>
                <SnackbarHelper>
                  <TutorialProvider>
                    <Box className="App">
                      <ApplicationSuiteProvider>
                        <MainLayout />
                      </ApplicationSuiteProvider>
                    </Box>
                  </TutorialProvider>
                </SnackbarHelper>
              </GreenChartSubscriptionRouteGuard>
            </ThemeProvider>
          }
        />
      </Routes>
    </Router>
  )
}

export default App
